import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { useCreateHostedFormMutation, useGetHostedFormsQuery } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { useFeature, useRegionId } from "store/selectors/hooks";

export const AgencySettingsHostedFormIndex: React.FC<unknown> = () => {
    const [isWorking, setIsWorking] = useState(false);
    const regionId = useRegionId();
    const hostedFormQuery = useGetHostedFormsQuery({ regionId });
    const router = useHistory();
    const createHostedFormMutation = useCreateHostedFormMutation();
    const createHostedForm = useCallback(async () => {
        setIsWorking(true);
        const result = await createHostedFormMutation.mutateAsync({});
        hostedFormQuery.refetch();
        router.push(`/settings/agency/hosted-form/${result.createHostedForm.id}`);
        setIsWorking(false);
    }, [hostedFormQuery, createHostedFormMutation, setIsWorking, router]);

    const hostedIntakeFormUnlimited = useFeature("hostedIntakeFormUnlimited");
    const productName = useProductName();

    return (
        <ContentFull>
            <ContentHeader>Hosted Intake Form</ContentHeader>
            <div className="space-y-4">
                <p>
                    Add new client records to {productName} by completing an intake form.
                    Share the direct link or embed the form on your website.
                </p>
                <QueryRenderer query={hostedFormQuery} name="AgencySettingsHostedForm">
                    {data => (
                        <>
                            {data.getHostedForms.length === 0 ? (
                                <PrimaryButton
                                    disabled={isWorking}
                                    onClick={createHostedForm}>
                                    {isWorking ? (
                                        <Spinner />
                                    ) : (
                                        <span>Create intake form</span>
                                    )}
                                </PrimaryButton>
                            ) : (
                                <div className="space-y-8 pt-8">
                                    <ul>
                                        {data.getHostedForms
                                            .sort((a, b) =>
                                                a.displayName.localeCompare(
                                                    b.displayName,
                                                ),
                                            )
                                            .map(form => (
                                                <li key={form.id}>
                                                    <Link
                                                        className={defaultLinkStyle}
                                                        to={`/settings/agency/hosted-form/${form.id}`}>
                                                        {form.displayName ?? (
                                                            <span>Untitled Form</span>
                                                        )}
                                                    </Link>
                                                </li>
                                            ))}
                                    </ul>
                                    {hostedIntakeFormUnlimited ? (
                                        <div>
                                            <PrimaryButton
                                                disabled={isWorking}
                                                onClick={createHostedForm}>
                                                {isWorking ? (
                                                    <Spinner />
                                                ) : (
                                                    <span>
                                                        Create another intake form
                                                    </span>
                                                )}
                                            </PrimaryButton>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </QueryRenderer>
            </div>
        </ContentFull>
    );
};
